import { useEffect, useRef, useState, useCallback } from "react";
import thumbnailAlpha from "../assets/thumbnail_alpha.jpeg";
import thumbnailIb from "../assets/thumbnail_ib.jpg";
import thumbnail3 from "../assets/thumbnail_3.jpg";

/** Parse Shorts / watch / youtu.be / embed URLs → 11-char id */
function extractYouTubeVideoId(url) {
  if (!url || typeof url !== "string") return null;
  const t = url.trim();
  try {
    const u = new URL(t.startsWith("//") ? `https:${t}` : t);
    const host = u.hostname.replace(/^www\./, "");
    if (host === "youtu.be") {
      const id = u.pathname.replace(/^\//, "").split("/")[0];
      return /^[\w-]{11}$/.test(id) ? id : null;
    }
    if (!host.endsWith("youtube.com")) return null;
    if (u.pathname.startsWith("/embed/")) {
      const id = u.pathname.slice(7).split("/")[0];
      return /^[\w-]{11}$/.test(id) ? id : null;
    }
    if (u.pathname.startsWith("/shorts/")) {
      const id = u.pathname.slice(8).split("/")[0];
      return /^[\w-]{11}$/.test(id) ? id : null;
    }
    const v = u.searchParams.get("v");
    if (v && /^[\w-]{11}$/.test(v)) return v;
  } catch {
    return null;
  }
  return null;
}

/** iframe embed src — controls=0 hides YouTube chrome; tap/click toggles play/pause */
function youtubeUrlToIframeSrc(url) {
  const id = extractYouTubeVideoId(url);
  if (!id) return "";
  const params = new URLSearchParams({
    autoplay: "1",
    playsinline: "1",
    rel: "0",
    modestbranding: "1",
    controls: "0",
    fs: "0",
    iv_load_policy: "3",
    disablekb: "0",
  });
  return `https://www.youtube.com/embed/${encodeURIComponent(id)}?${params}`;
}

const TESTIMONIALS = {
  featured: {
    id: "testimonial_01",
    parent: "Sowmya Iyer",
    context: "Mother of IB DP2 student",
    snippet:
      "I was introduced to Sparkl and I realized that this was the solution that we were looking for.",
    thumb: thumbnailIb,
    youtubeUrl: "https://www.youtube.com/shorts/9EhTE5RH8N4",
    duration: "1:09",
  },
  supporting: [
    {
      id: "testimonial_02",
      parent: "Chaitra Hegde",
      context: "Mother of Class-11 CBSE student",
      snippet:
        "We're very grateful as parents to the entire team at Sparkl and feel confident that Ahan is in the right place.",
      thumb: thumbnailAlpha,
      youtubeUrl: "https://www.youtube.com/shorts/cXmC0ldKmqk",
      duration: "2:09",
    },
    {
      id: "testimonial_03",
      parent: "Prerna Malik",
      context: "Mother of IB student",
      snippet:
        "The Sparkl team is very involved in helping your child succeed.",
      thumb: thumbnail3,
      youtubeUrl: "https://www.youtube.com/shorts/xsWcEcCYa0Y",
      duration: "1:46",
    },
  ],
};

// Heart positions (top%, left%, size px, color, drift duration s, opacity, delay s)
const HEARTS = [
  { t: 8, l: 6, s: 38, c: "rgba(255,216,191,0.32)", d: 14, o: 0.18, dl: 0 },
  { t: 18, l: 92, s: 28, c: "rgba(248,204,107,0.18)", d: 16, o: 0.16, dl: 1 },
  { t: 64, l: 4, s: 44, c: "rgba(151,216,196,0.18)", d: 18, o: 0.14, dl: 2 },
  { t: 78, l: 88, s: 32, c: "rgba(255,216,191,0.30)", d: 13, o: 0.16, dl: 0.5 },
  { t: 38, l: 50, s: 22, c: "rgba(248,204,107,0.16)", d: 17, o: 0.1, dl: 3 },
  { t: 88, l: 36, s: 26, c: "rgba(151,216,196,0.16)", d: 15, o: 0.12, dl: 1.5 },
  { t: 6, l: 56, s: 20, c: "rgba(255,216,191,0.22)", d: 19, o: 0.1, dl: 2.5 },
  { t: 50, l: 80, s: 36, c: "rgba(248,204,107,0.14)", d: 14, o: 0.12, dl: 0.8 },
  { t: 30, l: 24, s: 28, c: "rgba(151,216,196,0.14)", d: 18, o: 0.12, dl: 1.2 },
];

function HeartShape({ color, size }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 32 32"
      aria-hidden="true"
      style={{ display: "block" }}
    >
      <path
        d="M16 27.5s-9.5-5.7-12.4-12C1.7 11 4.3 6 9.2 6c2.6 0 4.9 1.4 6.8 3.8C17.9 7.4 20.2 6 22.8 6c4.9 0 7.5 5 5.6 9.5-2.9 6.3-12.4 12-12.4 12z"
        fill={color}
      />
    </svg>
  );
}

function PlayIcon({ size = 24, color = "#1E2430" }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true">
      <path d="M8 5.5v13l11-6.5L8 5.5z" fill={color} />
    </svg>
  );
}

export default function ParentTestimonialsSection() {
  const sectionRef = useRef(null);
  const headRef = useRef(null);
  const featuredRef = useRef(null);
  const featuredImgRef = useRef(null);
  const quoteRef = useRef(null);
  const heartsRef = useRef(null);
  const supportRefs = useRef([]);
  /** Open modal: iframe uses `youtubeUrl` (Shorts/watch/embed) */
  const [active, setActive] = useState(null);

  // Scroll-triggered reveal
  useEffect(() => {
    const els = [
      headRef.current,
      featuredRef.current,
      quoteRef.current,
      ...supportRefs.current,
    ].filter(Boolean);
    if (!("IntersectionObserver" in window)) {
      els.forEach((el) => el.classList.add("is-visible"));
      return undefined;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-visible");
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.18, rootMargin: "0px 0px -40px 0px" },
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);

  // Parallax — drive translateY on hearts, featured image, and quote panel.
  // Supporting cards stay untransformed so the two-up grid stays top-aligned.
  useEffect(() => {
    if (window.matchMedia("(max-width: 767px)").matches) return undefined;
    const sec = sectionRef.current;
    if (!sec) return undefined;
    let raf = 0;
    const update = () => {
      raf = 0;
      const rect = sec.getBoundingClientRect();
      const vh = window.innerHeight || 1;
      // -1 (above viewport) → 0 (centered) → +1 (below)
      const center = rect.top + rect.height / 2 - vh / 2;
      const p = Math.max(-1, Math.min(1, center / vh));

      if (heartsRef.current) {
        heartsRef.current.style.transform = `translate3d(0, ${p * -60}px, 0)`;
      }
      if (featuredImgRef.current) {
        featuredImgRef.current.style.transform = `scale(1.08) translate3d(0, ${p * -28}px, 0)`;
      }
      if (quoteRef.current) {
        quoteRef.current.style.setProperty("--parallax-y", `${p * 18}px`);
      }
    };
    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(update);
    };
    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  // Modal close + body scroll lock + ESC
  const closeModal = useCallback(() => {
    setActive(null);
  }, []);

  useEffect(() => {
    if (!active) {
      document.body.style.overflow = "";
      return undefined;
    }
    document.body.style.overflow = "hidden";
    const onKey = (e) => {
      if (e.key === "Escape") closeModal();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
    };
  }, [active, closeModal]);

  return (
    <section
      ref={sectionRef}
      className="sp-tt"
      id="testimonials"
      data-sp-snap-section
      data-testid="testimonials-section"
    >
      <div className="sp-tt-bg" aria-hidden="true" />

      {/* Heart ambience layer */}
      <div ref={heartsRef} className="sp-tt-hearts" aria-hidden="true">
        {HEARTS.map((h, i) => (
          <span
            key={i}
            className="sp-tt-heart"
            style={{
              top: `${h.t}%`,
              left: `${h.l}%`,
              opacity: h.o,
              animationDuration: `${h.d}s`,
              animationDelay: `${h.dl}s`,
            }}
          >
            <HeartShape color={h.c} size={h.s} />
          </span>
        ))}
      </div>

      <div className="sp-tt-inner">
        <header
          ref={headRef}
          className="sp-tt-head"
          data-testid="testimonials-head"
        >
          <span className="sp-tt-eyebrow">Parents Love Us</span>
          <h2 className="sp-tt-title">
            Parents <span className="sp-grad-word">love the way Sparkl</span>{" "}
            supports their child.
          </h2>
          <p className="sp-tt-sub">
            Hear directly from parents who have seen their child become more
            confident, more consistent, and more supported in their learning
            journey.
          </p>
        </header>

        <div className="sp-tt-main">
          {/* Featured video card */}
          <button
            ref={featuredRef}
            type="button"
            className="sp-tt-featured"
            onClick={() =>
              setActive({
                youtubeUrl: TESTIMONIALS.featured.youtubeUrl,
                title: `${TESTIMONIALS.featured.parent} — parent testimonial`,
              })
            }
            data-testid="featured-testimonial"
            aria-label={`Play testimonial from ${TESTIMONIALS.featured.parent}`}
          >
            <img
              ref={featuredImgRef}
              src={TESTIMONIALS.featured.thumb}
              alt={`${TESTIMONIALS.featured.parent}, parent testimonial`}
              loading="lazy"
            />
            <div className="sp-tt-featured-grad" aria-hidden="true" />
            <span className="sp-tt-play sp-tt-play--lg">
              <PlayIcon size={28} color="#1E2430" />
            </span>
            <span className="sp-tt-featured-meta">
              <span className="sp-tt-meta-dot" aria-hidden="true" />
              <span>Parent story · {TESTIMONIALS.featured.duration}</span>
            </span>
            <span className="sp-tt-featured-name">
              {TESTIMONIALS.featured.parent}
              <span className="sp-tt-featured-context">
                {TESTIMONIALS.featured.context}
              </span>
            </span>
          </button>

          {/* Quote panel */}
          <div
            ref={quoteRef}
            className="sp-tt-quote"
            data-testid="featured-quote"
          >
            <span className="sp-tt-quote-eyebrow">Parent story</span>
            <svg
              className="sp-tt-quote-mark"
              width="40"
              height="32"
              viewBox="0 0 40 32"
              aria-hidden="true"
            >
              <path
                d="M2 18C2 9.7 8.7 3 17 3v6.5c-4.7 0-8.5 3.8-8.5 8.5h2.5c2.5 0 4.5 2 4.5 4.5v3c0 2.5-2 4.5-4.5 4.5H6.5C4 30 2 28 2 25.5V18zm21 0c0-8.3 6.7-15 15-15v6.5c-4.7 0-8.5 3.8-8.5 8.5h2.5c2.5 0 4.5 2 4.5 4.5v3c0 2.5-2 4.5-4.5 4.5h-4.5C25 30 23 28 23 25.5V18z"
                fill="#FAAC00"
                opacity="0.85"
              />
            </svg>
            <p className="sp-tt-quote-text">{TESTIMONIALS.featured.snippet}</p>
            <div className="sp-tt-quote-meta">
              <div className="sp-tt-quote-avatar">
                <img src={TESTIMONIALS.featured.thumb} alt="" loading="lazy" />
              </div>
              <div>
                <div className="sp-tt-quote-name">
                  {TESTIMONIALS.featured.parent}
                </div>
                <div className="sp-tt-quote-context">
                  {TESTIMONIALS.featured.context}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Supporting cards row */}
        <div className="sp-tt-row" data-testid="supporting-row">
          {TESTIMONIALS.supporting.map((t, i) => (
            <button
              key={t.id}
              ref={(el) => (supportRefs.current[i] = el)}
              type="button"
              className="sp-tt-card"
              onClick={() =>
                setActive({
                  youtubeUrl: t.youtubeUrl,
                  title: `${t.parent} — parent testimonial`,
                })
              }
              data-testid={`supporting-card-${t.id}`}
              aria-label={`Play testimonial from ${t.parent}`}
              style={{ "--delay": `${i * 110 + 80}ms` }}
            >
              <div className="sp-tt-card-media">
                <img src={t.thumb} alt="" loading="lazy" />
                <div className="sp-tt-card-grad" aria-hidden="true" />
                <span className="sp-tt-play sp-tt-play--sm">
                  <PlayIcon size={18} color="#1E2430" />
                </span>
                <span className="sp-tt-card-duration">{t.duration}</span>
              </div>
              <div className="sp-tt-card-body">
                <p className="sp-tt-card-snippet">&ldquo;{t.snippet}&rdquo;</p>
                <div className="sp-tt-card-meta">
                  <span className="sp-tt-card-name">{t.parent}</span>
                  <span className="sp-tt-card-context">{t.context}</span>
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Modal lightbox */}
      {active ? (
        <div
          className="sp-tt-modal"
          role="dialog"
          aria-modal="true"
          data-testid="testimonial-modal"
          onClick={closeModal}
        >
          <button
            type="button"
            className="sp-tt-modal-close"
            onClick={(e) => {
              e.stopPropagation();
              closeModal();
            }}
            aria-label="Close video"
            data-testid="testimonial-modal-close"
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 18 18"
              aria-hidden="true"
            >
              <path
                d="M4 4l10 10M14 4L4 14"
                stroke="#1E2430"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          </button>
          <div
            className="sp-tt-modal-card"
            onClick={(e) => e.stopPropagation()}
          >
            <iframe
              key={active.youtubeUrl}
              title={active.title}
              className="sp-tt-modal-video sp-tt-modal-embed"
              src={youtubeUrlToIframeSrc(active.youtubeUrl)}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              loading="lazy"
            />
          </div>
        </div>
      ) : null}
    </section>
  );
}
